import { NetworkError } from '../../errors/NetworkError';
import { Fetcher } from '../../Fetcher';

import { APIS } from './constants';
import {
  parseCashbackScore,
  parsePrizeDropPrize,
  parsePrizeDropWinners,
  parsePromotionJSON,
  parseLeaderboardAroundMeDetails,
  parseLeaderboardDetails,
  parsePromotionWinNotificationJSON,
  parseBCPCard,
  parseBonusCardWinners,
} from './parsers';
import type {
  CancelledBonusCardAckParams,
  FetchCardsAndWinnersParams,
  FetchCashbackScoreParams,
  FetchLeaderboardAroundMeParams,
  FetchLeaderboardParams,
  FetchPrizesAndWinnersParams,
  FetchPromoJwtParams,
  FetchPromoParams,
  FetchPromoPointsParams,
  FetchRoundHistoryParams,
  PostPromoConsentParams,
  SetPromotionsScreenNameParams,
  WinPromoAckParams,
} from './types/promoFetcher';
import type {
  RoundHistoryType,
  RoundHistoryByWSType,
  FetchPromoData,
  FetchLeaderboardData,
  FetchPrizesAndWinnersData,
  FetchCashbackScoreData,
  FetchLeaderboardAroundMeData,
  FetchPromoConsentData,
  FetchCardsAndWinnersData,
} from './types/promotions';
import { PromotionType, PromotionOptedStatus } from './types/promotions';
import { ConsentStatusCode, PromoObjectType } from './types/promotionsApi';
import type {
  CardsAndWinnersAPIResponse,
  CashbackScoreAPIResponse,
  ConsentAPIResponse,
  InstantPointsApiResponse,
  LeaderboardAPIResponse,
  LeaderboardAroundMeAPIResponse,
  PrizesAndWinnersAPIResponse,
  PromotionsApiResponse,
} from './types/promotionsApi';

export const fetchPromotions = (params: FetchPromoParams): Promise<FetchPromoData> => {
  return Fetcher.fetch<PromotionsApiResponse>(`${params.envPromo}${APIS.FETCH_PROMOTIONS_INFO}`, {
    force: true,
    headers: { Authorization: `Bearer ${params.jwtToken}` },
    params: { isDetailed: true, lang: params.lang, tableId: params.gameId },
  }).then((response) => {
    switch (response.objectType) {
      case PromoObjectType.Promotions: {
        const resp = response as PromotionsApiResponse<PromoObjectType.Promotions>;
        return {
          promotions: resp.data.map((res) => parsePromotionJSON(res, params)).filter((item) => item !== null),
          winNotifications: [
            ...(resp.AdditionalData.TOURNAMENT_WIN_NOTIFICATION?.map((res) =>
              parsePromotionWinNotificationJSON(res, params)
            ) ?? []),
            ...(resp.AdditionalData.CASHBACK_WIN_NOTIFICATION?.map((res) =>
              parsePromotionWinNotificationJSON(res, params)
            ) ?? []),
          ],
        };
      }
      case 'ERROR': {
        const resp = response as PromotionsApiResponse<PromoObjectType.Error>;
        throw new NetworkError(resp.data);
      }
      default:
        throw new Error('Unknown `obType` field');
    }
  });
};

export const postPromoConsent = (params: PostPromoConsentParams): Promise<FetchPromoConsentData> => {
  return Fetcher.fetch<ConsentAPIResponse>(`${params.envPromo}${APIS.PROMO_OPT_INFO}`, {
    headers: {
      Authorization: `Bearer ${params.jwtToken}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
    body: {
      data: {
        opted: params.opted,
        tournamentIds: params.promotions.filter((p) => p.type === PromotionType.Tournament).map((p) => p.id),
        prizedropIds: params.promotions.filter((p) => p.type === PromotionType.Prizedrop).map((p) => p.id),
        cashbackIds: params.promotions.filter((p) => p.type === PromotionType.Cashback).map((p) => p.id),
        bonuscardIds: params.promotions.filter((p) => p.type === PromotionType.BonusCard).map((p) => p.id),
      },
    },
  }).then((response) => {
    // For Backward Compatibility to support old response.
    if (response.error) {
      const updatedPromotions = params.promotions.map((promo) => ({
        ...promo,
        opted: params.opted ? PromotionOptedStatus.Opted : PromotionOptedStatus.NonOpted,
      }));
      return { promotions: updatedPromotions };
    }
    const updatedPromotions = params.promotions.reduce(
      (acc, promo) => {
        const hasError = response.optInResponses.some(
          (res) => res.promoId === promo.id && res.errorCode !== ConsentStatusCode.SUCCESS
        );
        acc.push({
          ...promo,
          opted: params.opted && !hasError ? PromotionOptedStatus.Opted : PromotionOptedStatus.NonOpted,
        });
        return acc;
      },
      [] as typeof params.promotions
    );
    return { promotions: updatedPromotions };
  });
};

export const fetchPromotionsPoints = (params: FetchPromoPointsParams): Promise<InstantPointsApiResponse> => {
  return Fetcher.fetch<InstantPointsApiResponse>(`${params.envPromo}${APIS.FETCH_INSTANT_POINTS}`, {
    force: true,
    headers: { Authorization: `Bearer ${params.jwtToken}` },
  }).then((response) => {
    return response;
  });
};

export const fetchPromotionsJwt = (params: FetchPromoJwtParams): Promise<string> => {
  return Fetcher.fetch<{ jwtToken: string }>(`${params.envGs}${APIS.UPDATE_JWT_TOKEN}`, {
    params: { userId: params.userId, JSESSIONID: params.jsessionId },
  }).then((response) => response.jwtToken);
};

export const fetchLeaderboard = (params: FetchLeaderboardParams): Promise<FetchLeaderboardData | null> => {
  return Fetcher.fetch<LeaderboardAPIResponse>(`${params.envPromo}${APIS.LEADERBOARD}${params.tournamentId}`, {
    headers: { Authorization: `Bearer ${params.jwtToken}` },
    force: true,
  }).then((response) => {
    return {
      leaderboard: parseLeaderboardDetails(response),
      tournamentId: response.tournamentId,
    };
  });
};

export const fetchLeaderboardAroundMe = (
  params: FetchLeaderboardAroundMeParams
): Promise<FetchLeaderboardAroundMeData | null> => {
  return Fetcher.fetch<LeaderboardAroundMeAPIResponse>(
    `${params.envPromo}${APIS.LEADERBOARD_AROUND_ME}${params.tournamentId}/${params.casinoUserId}`,
    {
      headers: { Authorization: `Bearer ${params.jwtToken}` },
      force: true,
    }
  ).then((response) => {
    return {
      leaderboardAroundMe: parseLeaderboardAroundMeDetails(response),
      tournamentId: params.tournamentId,
    };
  });
};

export const fetchPrizesAndWinners = (params: FetchPrizesAndWinnersParams): Promise<FetchPrizesAndWinnersData> => {
  return Fetcher.fetch<PrizesAndWinnersAPIResponse>(`${params.envPromo}${APIS.PRIZES_AND_WINNERS}`, {
    headers: { Authorization: `Bearer ${params.jwtToken}` },
    force: true,
    params: { prizedropId: params.prizeDropId },
  }).then((response) => {
    return {
      prizes: parsePrizeDropPrize(response, params.prizeDropId),
      winners: parsePrizeDropWinners(response, params.prizeDropId),
      prizeDropId: params.prizeDropId,
      screenNameHidden: response[params.prizeDropId]?.screenNameHidden ?? false,
    };
  });
};

export const fetchCardsAndWinners = (params: FetchCardsAndWinnersParams): Promise<FetchCardsAndWinnersData> => {
  return Fetcher.fetch<CardsAndWinnersAPIResponse>(`${params.envPromo}${APIS.CARDS_AND_WINNERS}`, {
    headers: { Authorization: `Bearer ${params.jwtToken}` },
    force: true,
    params: { bonuscardId: params.bonuscardId },
  }).then((response) => {
    return {
      cards: parseBCPCard(response, params.bonuscardId),
      winners: parseBonusCardWinners(response, params.bonuscardId),
      bonuscardId: params.bonuscardId,
      screenNameHidden: response[params.bonuscardId]?.screenNameHidden ?? false,
    };
  });
};

export const fetchCashbackScore = (params: FetchCashbackScoreParams): Promise<FetchCashbackScoreData> => {
  return Fetcher.fetch<CashbackScoreAPIResponse>(`${params.envPromo}${APIS.FETCH_SCORE}`, {
    headers: { Authorization: `Bearer ${params.jwtToken}` },
    force: true,
    params: { cashbackId: params.cashbackId },
  }).then((response) => {
    return {
      score: parseCashbackScore(response, params.cashbackId),
      cahbackId: params.cashbackId,
    };
  });
};

export const fetchRoundHistory = (params: FetchRoundHistoryParams): Promise<RoundHistoryType[] | []> => {
  return Fetcher.fetch<RoundHistoryType[]>(`${params.envPromo}${APIS.FETCH_ROUND_HISTORY}`, {
    headers: { Authorization: `Bearer ${params.jwtToken}` },
    force: true,
    params: { tournamentId: params.tournamentId, lastId: params.winingStreakLastId, records: params.noOfRecords },
  }).then((response) => {
    return response;
  });
};

export const fetchRoundHistoryByWS = (params: FetchRoundHistoryParams): Promise<RoundHistoryByWSType[] | []> => {
  return Fetcher.fetch<RoundHistoryByWSType[]>(`${params.envPromo}${APIS.FETCH_ROUND_HISTORY_BY_WS}`, {
    headers: { Authorization: `Bearer ${params.jwtToken}` },
    force: true,
    params: { tournamentId: params.tournamentId, lastId: params.winingStreakLastId, records: params.noOfRecords },
  }).then((response) => {
    return response;
  });
};

export const winPromoAck = (params: WinPromoAckParams): void => {
  Fetcher.fetch(`${params.envPromo}${APIS.PROMO_ACK}`, {
    headers: { Authorization: `Bearer ${params.jwtToken}` },
    params: { promoId: params.promoId, type: params.promoType },
    force: true,
  });
};

export const cancelledBonusCardAck = (params: CancelledBonusCardAckParams): void => {
  Fetcher.fetch(`${params.envPromo}${APIS.BONUS_CARD_CANCELLED_ACK}?bonusCardId=${params.promoId}`, {
    headers: {
      Authorization: `Bearer ${params.jwtToken}`,
      'Content-Type': 'application/json',
    },
  });
};

export const setPromotionsScreenName = (params: SetPromotionsScreenNameParams): void => {
  Fetcher.fetch<void>(`${params.envPromo}${APIS.UPDATE_SCREEN_NAME}`, {
    headers: {
      Authorization: `Bearer ${params.jwtToken}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
  });
};
